import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  accrualTypes, visibleAccrualTypes, VALUE_KIND_LABELS,
  type AccrualType, type AccrualTypeCreate,
} from '@/api/hr';
import { useConfirm } from '@/components/ConfirmProvider';
import Drawer from '@/components/Drawer';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

/**
 * Довідник видів нарахувань (tenant-scoped, фіча #3).
 *
 * Вид визначає СПОСIБ розрахунку (FIXED_SUM/PERCENT/RATE) + чи базовий (оклад) +
 * чи входить у базу для %. Дефолтні види (is_seed 🔒) не можна видалити.
 * Використовуються у планових нарахуваннях працівника.
 * Permission: refs.accrual_types.manage (enforcer + RLS tenant-scoped).
 */
const CATEGORY_LABELS: Record<string, string> = {
  BASE: 'Базова оплата',
  SUPPLEMENT: 'Надбавка/доплата',
  PREMIUM: 'Премія',
  INDEX: 'Індексація',
};

interface FormState {
  id: string | null;
  code: string;
  name: string;
  value_kind: 'FIXED_SUM' | 'PERCENT' | 'RATE';
  category: 'BASE' | 'SUPPLEMENT' | 'PREMIUM' | 'INDEX';
  is_base_salary: boolean;
  is_base_for_percent: boolean;
  is_time_proportional: boolean;
  calc_order: number;
  is_active: boolean;
}

const EMPTY: FormState = {
  id: null, code: '', name: '', value_kind: 'FIXED_SUM', category: 'SUPPLEMENT',
  is_base_salary: false, is_base_for_percent: false, is_time_proportional: true,
  calc_order: 50, is_active: true,
};

export default function AccrualTypesPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);

  const list = useQuery({ queryKey: ['accrual-types'], queryFn: accrualTypes.list });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['accrual-types'] });

  const save = useMutation({
    mutationFn: () => {
      const f = form!;
      const payload: AccrualTypeCreate = {
        code: f.code.trim(), name: f.name.trim(), value_kind: f.value_kind,
        category: f.category, is_base_salary: f.is_base_salary,
        is_base_for_percent: f.is_base_for_percent,
        is_time_proportional: f.is_time_proportional,
        calc_order: f.calc_order, is_active: f.is_active,
      };
      if (f.id) {
        const { code: _c, ...rest } = payload;
        return accrualTypes.update(f.id, rest);
      }
      return accrualTypes.create(payload);
    },
    onSuccess: () => { invalidate(); setForm(null); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => accrualTypes.remove(id),
    onSuccess: () => { invalidate(); setForm(null); },
  });

  const startEdit = (t: AccrualType) => setForm({
    id: t.id, code: t.code, name: t.name, value_kind: t.value_kind,
    category: t.category, is_base_salary: t.is_base_salary,
    is_base_for_percent: t.is_base_for_percent,
    is_time_proportional: t.is_time_proportional, calc_order: t.calc_order,
    is_active: t.is_active,
  });

  // Показуємо лише 4 системних види + імпортовані/користувацькі (рішення власника).
  const rows = visibleAccrualTypes(list.data);
  const canSave = !!form && !!form.code.trim() && !!form.name.trim() && !save.isPending;

  const cols = useMemo<ColumnDef<AccrualType>[]>(() => [
    {
      key: 'code', header: 'Код', width: 160,
      sortValue: (t) => t.code ?? '',
      cell: (t) => <>{t.is_seed && '🔒 '}{t.code}</>,
    },
    {
      // Без width — гнучка колонка: займає вільний простір.
      key: 'name', header: 'Назва',
      sortValue: (t) => (t.name ?? '').toLowerCase(), cell: (t) => t.name,
    },
    {
      key: 'value_kind', header: 'Спосіб', width: 180, align: 'center',
      sortValue: (t) => VALUE_KIND_LABELS[t.value_kind] ?? t.value_kind ?? '',
      cell: (t) => VALUE_KIND_LABELS[t.value_kind],
    },
    {
      key: 'category', header: 'Категорія', width: 170, align: 'center',
      sortValue: (t) => CATEGORY_LABELS[t.category] ?? t.category ?? '',
      cell: (t) => CATEGORY_LABELS[t.category],
    },
    {
      key: 'is_base_salary', header: 'Базовий', width: 90, align: 'center',
      sortValue: (t) => (t.is_base_salary ? 1 : 0),
      cell: (t) => t.is_base_salary ? '✓' : '',
    },
    {
      key: 'actions', header: '', width: 90, align: 'center',
      cell: (t) => !t.is_seed && (
        <button className="danger" title="Видалити"
          onClick={() => confirm(`Видалити вид «${t.name}»?`).then((ok) => {
            if (ok) remove.mutate(t.id);
          })}>✕</button>
      ),
    },
  ], [confirm, remove]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Види нарахувань</h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
                onClick={() => setForm({ ...EMPTY })}>
          + Новий
        </button>
      </div>
      <p style={{ color: 'var(--muted)', maxWidth: 760 }}>
        Вид нарахування визначає спосіб розрахунку (фіксована сума / відсоток /
        тариф). Дефолтні види позначені 🔒. Використовуються у планових
        нарахуваннях працівника (оклад, надбавки, доплати, премії).
      </p>

      <DataTable<AccrualType>
        rows={rows}
        columns={cols}
        rowKey={(t) => t.id}
        loading={list.isLoading}
        emptyText="Немає видів нарахувань."
        onRowDoubleClick={startEdit}
        rowMuted={(t) => t.is_active ? undefined : 0.5}
        initialSortKey="name"
      />

      {form && (
        <Drawer
          open={!!form}
          title={form.id ? 'Редагувати вид нарахування' : 'Новий вид нарахування'}
          onClose={() => setForm(null)}
          footer={
            <>
              <button onClick={() => setForm(null)}>Скасувати</button>
              <button className="primary" disabled={!canSave} onClick={() => save.mutate()}>
                Зберегти
              </button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label>Код
              <input value={form.code} disabled={!!form.id}
                onChange={(e) => setForm({ ...form, code: e.target.value })} />
            </label>
            <label>Назва
              <input value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label>Спосіб розрахунку
              <select value={form.value_kind}
                onChange={(e) => setForm({ ...form, value_kind: e.target.value as FormState['value_kind'] })}>
                {Object.entries(VALUE_KIND_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </label>
            <label>Категорія
              <select value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value as FormState['category'] })}>
                {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </label>
            <label>Черговість обчислення (1-100, база=10, % від бази=50+)
              <input type="number" min={1} max={100} value={form.calc_order}
                onChange={(e) => setForm({ ...form, calc_order: Number(e.target.value) })} />
            </label>
            <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input type="checkbox" checked={form.is_base_salary}
                onChange={(e) => setForm({ ...form, is_base_salary: e.target.checked })} />
              Базовий вид (оклад/тариф — рахується першим)
            </label>
            <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input type="checkbox" checked={form.is_base_for_percent}
                onChange={(e) => setForm({ ...form, is_base_for_percent: e.target.checked })} />
              Входить у базу для відсоткових нарахувань
            </label>
            <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input type="checkbox" checked={form.is_time_proportional}
                onChange={(e) => setForm({ ...form, is_time_proportional: e.target.checked })} />
              Пропорційно відпрацьованому часу (неповний місяць)
            </label>
            <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input type="checkbox" checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Активний
            </label>
          </div>
        </Drawer>
      )}
    </div>
  );
}
